import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Disc3, Loader2 } from "lucide-react";
import { HERO_IMG } from "@/lib/api";

function formatError(detail) {
    if (!detail) return "Sign-in failed. Try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map(d => d?.msg || JSON.stringify(d)).join(" ");
    return String(detail);
}

export default function LoginPage() {
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [remember, setRemember] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await login(email.trim(), password, remember);
        } catch (err) {
            setError(formatError(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-stretch" data-testid="login-page">
            {/* Visual side */}
            <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
                <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-br from-foreground/70 via-foreground/40 to-foreground/80" />
                <div className="relative h-full flex flex-col justify-between p-12 text-background">
                    <div className="flex items-center gap-3">
                        <Disc3 className="w-7 h-7 vinyl-spin" strokeWidth={1.5} />
                        <div className="leading-none">
                            <p className="font-display font-bold text-lg tracking-tight">Wax & Sleeves</p>
                            <p className="overline opacity-70 mt-1">Vinyl Archive</p>
                        </div>
                    </div>
                    <div>
                        <h1 className="font-display text-5xl xl:text-6xl font-bold leading-none tracking-tight">
                            Welcome back<br />
                            <span className="italic font-medium opacity-80">to the shelf.</span>
                        </h1>
                        <p className="mt-6 max-w-md leading-relaxed opacity-80">
                            A private archive for Ruth & Andy. Sign in to browse, add and care for the collection.
                        </p>
                    </div>
                </div>
            </div>

            {/* Form side */}
            <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-background bg-grain">
                <div className="w-full max-w-sm space-y-8">
                    <div className="lg:hidden flex items-center gap-3">
                        <Disc3 className="w-7 h-7 text-primary vinyl-spin" strokeWidth={1.5} />
                        <p className="font-display font-bold text-lg tracking-tight">Wax & Sleeves</p>
                    </div>

                    <div>
                        <p className="overline text-muted-foreground mb-3">Sign in</p>
                        <h2 className="font-display text-3xl sm:text-4xl font-bold leading-none tracking-tight">
                            Enter the archive.
                        </h2>
                        <p className="text-muted-foreground mt-3 text-sm">
                            Private access for Ruth & Andy only.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Label htmlFor="email" className="overline">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                autoComplete="username"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="mt-1.5 h-11"
                                required
                                data-testid="login-email"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="overline">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="mt-1.5 h-11"
                                required
                                data-testid="login-password"
                            />
                        </div>

                        {error && (
                            <p className="text-sm text-destructive" data-testid="login-error">
                                {error}
                            </p>
                        )}

                        <div className="flex items-start gap-2.5 pt-1">
                            <Checkbox
                                id="remember"
                                checked={remember}
                                onCheckedChange={(v) => setRemember(v === true)}
                                className="mt-0.5"
                                data-testid="login-remember"
                            />
                            <div className="space-y-0.5 leading-tight">
                                <Label htmlFor="remember" className="text-sm font-medium cursor-pointer">
                                    Remember this device
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                    Stay signed in for 90 days. Skip on shared or public devices.
                                </p>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full h-11 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
                            data-testid="login-submit"
                        >
                            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                            Sign in
                        </Button>
                    </form>

                    <p className="text-xs text-muted-foreground text-center">
                        Forgot the password? Ask the keeper of the shelf.
                    </p>
                </div>
            </div>
        </div>
    );
}
